import {
  createDashboardTab,
  deleteDashboardTab,
  updateDashboardTab,
  reorderDashboardTabs,
  retrieveDashboardTabs,
} from "@/lib/data/requests/matters/dashboard-tabs";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { DashboardTabView } from "@/lib/types/MatterDashboardTab";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

export const dashboardTabsQueryKey = (
  firmId: string,
  userId: string,
  view: DashboardTabView,
) => [queryKeys.matterDashboardTabs, firmId, userId, view] as const;

export const dashboardTabsQueryOptions = (
  firmId: string,
  userId: string,
  view: DashboardTabView,
) =>
  queryOptions({
    queryKey: dashboardTabsQueryKey(firmId, userId, view),
    queryFn: () => retrieveDashboardTabs(view),
    refetchOnWindowFocus: true,
  });

export function useDashboardTabMutations(firmId: string, userId: string) {
  const queryClient = useQueryClient();
  const queryKey = [queryKeys.matterDashboardTabs, firmId, userId];
  const refresh = () => queryClient.invalidateQueries({ queryKey });
  return {
    createTab: useMutation({
      mutationFn: createDashboardTab,
      onSuccess: refresh,
    }),
    updateTab: useMutation({
      mutationFn: updateDashboardTab,
      onSuccess: refresh,
    }),
    reorderTabs: useMutation({
      mutationFn: reorderDashboardTabs,
      onSuccess: (response) => {
        queryClient.setQueryData(
          dashboardTabsQueryKey(firmId, userId, "all"),
          response,
        );
      },
      onSettled: refresh,
    }),
    deleteTab: useMutation({
      mutationFn: deleteDashboardTab,
      onSuccess: refresh,
    }),
  };
}
